import { FORM_CHECKBOX, FORM_INPUT, FORM_RADIO, FORM_SELECT } from "./DataTypes";
import {
  FORM_STYLE_CHECKBOXES,
  FORM_STYLE_DROPDOWN,
  FORM_STYLE_FIELD,
  FORM_STYLE_RADIOS,
} from "./FormFieldConstants.mjs";

export const DEFAULT_FORM_FONT = "Montserrat";

/** What the renderer draws a form field in when no font is set. */
export const FORM_FALLBACK_FONT = "Arial";

const REQUIRED_COLOR = "#D74242";
const ERROR_COLOR = "#E74D3C";

/** @typedef {import("@/types/data/FormStyle.mjs").FormStyle} FormStyle */

/**
 * Builds the default style entry for every form type. `field` is the base entry and carries the
 * whole shape; the other three point at it through their top-level `style` and carry only what
 * their type adds or overrides, leaving the rest to the theme entry chain. A state (`focus`,
 * `error`) carries only what differs from Default, and everything else is read from Default. Only
 * a stored state value is listed in `sync`; an unset one takes its sync from Default.
 * @param {string} font
 * @returns {Record<string, FormStyle>}
 */
export const createFormStyleDefaults = (font) => ({
  [FORM_STYLE_FIELD]: {
    id: FORM_STYLE_FIELD,
    name: "Field",
    type: FORM_INPUT,
    desktop: {
      control: {
        bgcolor: "#ffffff",
        bC: "#ABABAB",
        bW: 2,
        bS: "solid",
        border: { rad: 6 },
        padding: "14px 14px 14px 14px",
        shadow: { style: "none", sc: "#000000:50", sx: 2, sy: 2, sb: 6 },
        text: { font, size: 16, color: "#000000", placeholderColor: "#ABABAB" },
      },
      label: {
        position: "above",
        requiredPlacement: "after",
        gap: 10,
        width: 120,
        font,
        size: 16,
        color: "#000000",
        requiredColor: REQUIRED_COLOR,
      },
      focus: { control: { bC: "#82C7E3" } },
      error: { control: { bC: ERROR_COLOR }, label: { color: ERROR_COLOR } },
    },
    mobile: {},
    sync: [
      "control.bgcolor",
      "control.bC",
      "control.bW",
      "control.bS",
      "control.border.rad",
      "control.padding",
      "control.shadow.style",
      "control.text.font",
      "control.text.size",
      "control.text.color",
      "control.text.placeholderColor",
      "label.position",
      "label.requiredPlacement",
      "label.gap",
      "label.width",
      "label.font",
      "label.size",
      "label.color",
      "label.requiredColor",
      "focus.control.bC",
      "error.control.bC",
      "error.label.color",
    ],
  },

  [FORM_STYLE_DROPDOWN]: {
    id: FORM_STYLE_DROPDOWN,
    name: "Dropdown",
    type: FORM_SELECT,
    style: FORM_STYLE_FIELD,
    desktop: {
      control: {
        padding: "14px 40px 14px 14px",
        arrow: { style: "chevron", color: "#000000", size: 22 },
      },
    },
    mobile: {},
    sync: ["control.padding", "control.arrow.style", "control.arrow.color", "control.arrow.size"],
  },

  [FORM_STYLE_RADIOS]: {
    id: FORM_STYLE_RADIOS,
    name: "Radios",
    type: FORM_RADIO,
    style: FORM_STYLE_FIELD,
    desktop: {
      control: {
        bgcolor: "#ffffff",
        bW: 1,
        border: { rad: "50%" },
        padding: "3px 3px 3px 3px",
        radio: { style: "filled", dotColor: "#000000", dotSize: 6 },
      },
      radio: { direction: "vertical", gap: 14 },
    },
    mobile: {},
    sync: [
      "control.bgcolor",
      "control.bW",
      "control.border.rad",
      "control.padding",
      "radio.direction",
      "radio.gap",
      "control.radio.style",
      "control.radio.dotColor",
      "control.radio.dotSize",
    ],
  },

  [FORM_STYLE_CHECKBOXES]: {
    id: FORM_STYLE_CHECKBOXES,
    name: "Checkboxes",
    type: FORM_CHECKBOX,
    style: FORM_STYLE_FIELD,
    desktop: {
      control: {
        bgcolor: "#ffffff",
        border: { rad: 4 },
        checkbox: { style: "checkmark" },
      },
      label: { position: "inline" },
    },
    mobile: {},
    sync: ["control.bgcolor", "control.border.rad", "control.checkbox.style", "label.position"],
  },
});

export default createFormStyleDefaults;
